import React, { useLayoutEffect, useState, useMemo } from 'react'
import FilterGroup from './filter_group.jsx'
import { RangeInput } from 'lensrentals-components'
import cx from 'classnames'
import styles from '../../assets/stylesheets/pages/searches/filters.module.scss'
import useDebouncedCallback from '../misc/use_debounced_callback.jsx'
import PropTypes from 'prop-types'

export const SliderMinMaxFilterUtils = () => {
  const getSliderMinAndMaxIndexes = (selectedFilters, range, name) => {
    const filterType = 'number_range_filters'
    let minIndex; let maxIndex = null
    if (selectedFilters) {
      const currentSelectedFilters = JSON.parse(selectedFilters)
      const existingMinFilter = currentSelectedFilters[filterType].find(filter => filter.name === `${name} (min)`)
      if (existingMinFilter && existingMinFilter.gte) {
        const currentIndex = range.indexOf(parseFloat(existingMinFilter.gte))
        if (currentIndex === -1) {
          minIndex = 0
        } else {
          minIndex = currentIndex
        }
      } else {
        minIndex = 0
      }
      const existingMaxFilter = currentSelectedFilters[filterType].find(filter => filter.name === `${name} (max)`)
      if (existingMaxFilter && existingMaxFilter.lte) {
        const currentIndex = range.indexOf(parseFloat(existingMaxFilter.lte))
        if (currentIndex === -1) {
          maxIndex = range.length - 1
        } else {
          maxIndex = currentIndex
        }
      } else {
        maxIndex = range.length - 1
      }
    }
    return [minIndex, maxIndex]
  }

  const findClosestMaxValue = (minValue, minRange, maxValue, maxRange) => {
    const lastMaxValue = maxRange[maxRange.length - 1]
    let newMaxValue = maxRange.find(value => (value <= lastMaxValue && value >= maxValue) && value > minValue)
    if (newMaxValue !== undefined) {
      return [minValue, newMaxValue]
    } else {
      //  check if there is value < maxValue or same value as minValue
      newMaxValue = maxRange.find(value => (value <= lastMaxValue && value < maxValue) && value >= minValue)
      if (newMaxValue !== undefined) {
        return [minValue, newMaxValue]
      } else {
        //  use biggest min and max values, to make sure search return any results
        return [minRange[minRange.length - 1], maxRange[maxRange.length - 1]]
      }
    }
  }

  const findClosestMinValue = (minValue, minRange, maxValue, maxRange) => {
    const firstMinValue = minRange[0]
    let newMinValue = minRange.find(value => (value >= firstMinValue && value >= minValue) && value < maxValue)
    if (newMinValue !== undefined) {
      return [newMinValue, maxValue]
    } else {
      //  check if here is value < minValue or same value as maxValue
      newMinValue = minRange.sort((a, b) => b - a).find(value => (value >= firstMinValue && value < minValue) && value <= maxValue)
      if (newMinValue !== undefined) {
        return [newMinValue, maxValue]
      } else {
        //  use biggest min and max values, to make sure search return any results
        return [minRange[minRange.length - 1], maxRange[maxRange.length - 1]]
      }
    }
  }

  const adjustMinAndMax = (minValue, minRange, maxValue, maxRange) => {
    if (minValue > maxValue) {
      const currentMinValue = minValue
      minValue = maxValue
      maxValue = currentMinValue
    }
    //  check if values are valid for both min and max
    const minVal = minRange.find(value => value === minValue)
    const maxVal = maxRange.find(value => value === maxValue)
    if (minVal !== undefined && maxVal !== undefined) {
      return [minVal, maxVal]
    } else if (minVal === undefined && maxVal === undefined) {
      const [min, max] = findClosestMaxValue(minValue, minRange, maxValue, maxRange)
      return findClosestMinValue(min, minRange, max, maxRange)
    } else if (minVal !== undefined) {
      //  find closest max value that exist in maxArray
      const [min, max] = findClosestMaxValue(minValue, minRange, maxValue, maxRange)
      return [min, max]
    } else {
      const [min, max] = findClosestMinValue(minValue, minRange, maxValue, maxRange)
      return [min, max]
    }
  }
  return {
    getSliderMinAndMaxIndexes,
    findClosestMaxValue,
    findClosestMinValue,
    adjustMinAndMax
  }
}

const SliderMinMaxFilter = props => {
  const {
    name,
    values,
    selectMinMaxFilter,
    selectedFilters,
    handleFilterSelection,
    isOpen,
    toggleOpenFilters,
    isExpanded,
    toggleExpandedFilters
  } = props

  const selectMinMaxFilerDebounced = useDebouncedCallback(
    (selectedFilters, name, adjustedMinValue, adjustedMaxVal, event) => handleFilterSelection(selectMinMaxFilter(selectedFilters, name, adjustedMinValue, adjustedMaxVal), event),
    500
  )

  // const range = values.min.filter(value => values.max.includes(value)).sort((a, b) => a - b)
  // use set to de-duplicate values
  const range = useMemo(() => ([
    ...new Set([
      ...values.min,
      ...values.max
    ]
    )].sort((a, b) => a - b)), [values.toString()])

  const minRange = [
    ...new Set([
      ...values.min
    ]
    )].sort((a, b) => a - b)

  const maxRange = [
    ...new Set([
      ...values.max
    ]
    )].sort((a, b) => a - b)

  const handleChange = event => {
    let minValue, maxValue
    event.persist()
    const newValue = range[event.target.value]
    if (event.target.name === `${name}-min`) {
      minValue = newValue
      maxValue = (maxIndex == null) ? range[range.length - 1] : range[maxIndex]
    } else if (event.target.name === `${name}-max`) {
      maxValue = newValue
      minValue = (minIndex == null) ? range[0] : range[minIndex]
    }
    //  check if values are valid for both min and max
    const [adjustedMinValue, adjustedMaxValue] = SliderMinMaxFilterUtils().adjustMinAndMax(minValue, minRange, maxValue, maxRange)
    setMinIndex(range.indexOf(adjustedMinValue))
    setMaxIndex(range.indexOf(adjustedMaxValue))
    selectMinMaxFilerDebounced(selectedFilters, name, adjustedMinValue, adjustedMaxValue, event)
  }

  const showCurrentRange = () => {
    return `${range[minIndex]} - ${range[maxIndex]}`
  }

  const [minIndex, setMinIndex] = useState(0)
  const [maxIndex, setMaxIndex] = useState(range.length - 1)

  useLayoutEffect(() => {
    const [minInd, maxInd] = SliderMinMaxFilterUtils().getSliderMinAndMaxIndexes(selectedFilters, range, name)
    setMinIndex(minInd)
    setMaxIndex(maxInd)
  }, [selectedFilters, range, name])

  return (
    <FilterGroup
      name={name}
      isOpen={isOpen}
      toggleOpenFilters={toggleOpenFilters}
      isExpanded={isExpanded}
      toggleExpandedFilters={toggleExpandedFilters}
    >
      <RangeInput
        min-max
        className={cx(styles['filter--slider'], styles['filter--slider-min-max'])}
        name={name}
        id={name.replace(/\s+/g, '-')}
        min={0}
        max={range.length - 1}
        value={[minIndex, maxIndex]}
        onChange={handleChange}
      />
      <span className={`${name.replace(/\s+/g, '-')}-span`}>
        {showCurrentRange()}
      </span>
    </FilterGroup>
  )
}
SliderMinMaxFilter.propTypes = {
  name: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
  selectMinMaxFilter: PropTypes.func.isRequired,
  selectedFilters: PropTypes.string.isRequired,
  handleFilterSelection: PropTypes.func.isRequired
}

export default SliderMinMaxFilter
